import {
  ExtendedAccountInfo,
  JsonSchemaPropertyMap,
  LandingPageDropdownErrors,
  LandingPageOptions,
  PrimitiveValueType,
  RowData,
  RowDataObject,
  RowScopedProperties,
  SetupListNavInfoByDepartment,
} from "../../../types";
import { SCHEMA_TITLES } from "../constants";

const departmentMap: Record<string, string> = {
  accounting: "-A",
  parts: "-I",
  sales: "-SL",
  service: "-S",
};

const getAccountsForStore = (
  store: string,
  extendedAccountInfo: ExtendedAccountInfo[],
): string[] => {
  return (
    (extendedAccountInfo.find((item) => item.storeId === store)
      ?.accounts as string[]) ?? []
  );
};

export const getStoreLevelAccountName = (
  store: string,
  department: string,
  extendedAccountInfo: ExtendedAccountInfo[],
  setupListNavInfoByDepartment?: SetupListNavInfoByDepartment,
): string => {
  const shouldFetchDepartmentNameFromNavInfo =
    department.toLocaleLowerCase() === "parts" ||
    department.toLocaleLowerCase() === "service";

  if (shouldFetchDepartmentNameFromNavInfo) {
    const departmentIdInfo = setupListNavInfoByDepartment?.[
      department
    ].departmentIds.find((departmentId) => departmentId.storeId === store);
    return departmentIdInfo ? departmentIdInfo.name : "";
  }

  const storeAccounts = getAccountsForStore(store, extendedAccountInfo);
  const suffix = departmentMap[department.toLowerCase()];
  return (
    storeAccounts.find((account) => account.endsWith(suffix)) ??
    storeAccounts[0] ??
    ""
  );
};

export const getEnterpriseLevelAccountNamesArray = (
  selectedDepartment: string,
  extendedAccountInfo: ExtendedAccountInfo[],
): string[] => {
  const enterpriseAccounts = extendedAccountInfo
    .map((item) => item.accounts)
    .flat();
  const suffix = departmentMap[selectedDepartment.toLowerCase()];

  const filteredAccounts = enterpriseAccounts.filter((account) =>
    account.endsWith(suffix),
  );
  const uniqueFilteredAccounts = [...new Set(filteredAccounts)];

  return uniqueFilteredAccounts;
};

export const validateAndCheckLandingPageOptions = (
  selected: LandingPageOptions,
): {
  isValid: boolean;
  landingPageDropdownErrors: LandingPageDropdownErrors;
} => {
  const { store, department, setup } = selected;
  const hasStore = store.value !== "";
  const hasDepartment = department.value !== "";
  const hasSetup = setup.value !== "";

  const errors: LandingPageDropdownErrors = {
    store: { error: !hasStore },
    department: { error: !hasDepartment },
    setup: { error: !hasSetup },
  };

  const isValid = hasStore && hasDepartment && hasSetup;

  return { isValid, landingPageDropdownErrors: errors };
};

export const dynamicallyUpdateLandingPageErrors = (
  selected: LandingPageOptions,
  prev: LandingPageDropdownErrors,
): LandingPageDropdownErrors => {
  const { store, department, setup } = selected;
  const updatedErrors = { ...prev };
  let hasChanges = false;

  if (store.value !== "" && prev.store.error) {
    updatedErrors.store.error = false;
    hasChanges = true;
  }

  if (department.value !== "" && prev.department.error) {
    updatedErrors.department.error = false;
    hasChanges = true;
  }

  if (setup.value !== "" && prev.setup.error) {
    updatedErrors.setup.error = false;
    hasChanges = true;
  }

  return hasChanges ? updatedErrors : prev;
};

export const shouldRenderTab = (
  isRecordOpen: boolean,
  setupName: string,
): boolean => {
  const SETUPS_NEED_HIDING_TAB_IN_RECORD_PAGE = [
    SCHEMA_TITLES.VENDOR_CONFIGURATION_GROUP,
  ];

  if (SETUPS_NEED_HIDING_TAB_IN_RECORD_PAGE.includes(setupName) && isRecordOpen)
    return false;

  return true;
};

export function getDepartmentSpecificHeaders({
  groupId,
  departmentId,
  functionalArea,
}: {
  groupId: string | undefined;
  departmentId: string | undefined;
  functionalArea: string;
}): Record<string, string> {
  switch (functionalArea) {
    case "accounting":
      return { "x-group-id": groupId ?? "" };
    case "parts":
    case "service":
      return { "x-department-id": departmentId ?? "" };
    default:
      return {};
  }
}

export const getCorrelationPropertyContext = ({
  properties,
  rowScopedProperties,
  propertySubKey,
  propertyKey,
  rowIndex,
  inputValueObject,
  rowData,
}: {
  properties: JsonSchemaPropertyMap;
  rowScopedProperties?: RowScopedProperties;
  propertySubKey: string;
  propertyKey: string;
  rowIndex: number;
  inputValueObject: RowDataObject[] | RowData;
  rowData: RowData;
}) => {
  const updatedItemProperty = rowScopedProperties?.[rowIndex]?.[propertySubKey] ||
      properties[propertySubKey]

  const coRelationInputValues = Array.isArray(inputValueObject)
    ? inputValueObject
    : [];

  const inputValue = (coRelationInputValues[rowIndex]?.[propertySubKey] as PrimitiveValueType) ?? null;

  return {
    updatedItemProperty,
    inputValue: inputValue as PrimitiveValueType,
    dynamicPropertyKey: propertyKey,
    dynamicRowData: rowData,
  };
};

export const getObjectPropertyContext = ({
  properties,
  propertyKey,
  propertySubKey,
  inputValueObject,
  rowData,
}: {
  properties: JsonSchemaPropertyMap;
  propertyKey: string;
  propertySubKey: string;
  inputValueObject: RowDataObject[] | RowData;
  rowData: RowData;
}) => {
  const updatedItemProperty = properties[propertySubKey];

  const objectInputValues = Array.isArray(inputValueObject)
    ? ({} as RowData)
    : inputValueObject;

  const inputValue =
    (objectInputValues?.[propertySubKey] as PrimitiveValueType) ??
    updatedItemProperty.default ??
    null;
  const dynamicRowData = (rowData[propertyKey] as RowData) ?? {};

  return {
    updatedItemProperty,
    inputValue,
    dynamicPropertyKey: propertySubKey,
    dynamicRowData,
  };
};
